import { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import Select from "react-select";
import Pagination from "@mui/material/Pagination";
import { authContext } from "../contexts/AuthProvider";
import { loaderContext } from "../contexts/LoaderProvider";
import Loader from "../layout/Loader";
import Header from "../layout/Header";
import Sidebar from "../layout/Sidebar";
import Footer from "../layout/Footer";
import ScrollToTop from "../layout/ScrollToTop";
import LoadScript from "../utils/LoadScript";
import {
  fetchBranches,
  createNewBranch,
  editBranch,
  archiveBranch,
  restoreBranch,
} from "../services/branchService";
import {
  fetchAreaList,
  fetchBlockList,
  fetchStreetList,
} from "../services/locationService";
import { dataContext } from "../contexts/DataProvider";
import { errorContext } from "../contexts/ErrorProvider";
import ProcessData from "../utils/ProcessData";
import validate from "../validation/validator";
import { branchSchema } from "../validation/schema";
import AddressMap from "../utils/AddressMap";

const AllBranches = () => {
  const [data, setData] = useContext(dataContext);
  const [errors, setErrors] = useContext(errorContext);
  const [branches, setBranches] = useState([]);
  const [areas, setAreas] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [streets, setStreets] = useState([]);
  const [modal, setModal] = useState({ open: false, action: "", data: "" });
  const { isLoading, setIsLoading } = useContext(loaderContext);
  const [isAuth] = useContext(authContext);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const getBranches = useCallback(async () => {
    setIsLoading(true);
    const response = await fetchBranches(page, limit, search);
    if (response.data.code === 1) {
      setBranches(response.data.data);
      setTotalPages(response.data.pages);
      setIsLoading(false);
    }
  }, [page, search, limit, setIsLoading]);

  const add = useCallback(async () => {
    const validation = validate(data, setErrors, branchSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await createNewBranch(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getBranches();
      } else {
        setErrors({ ...errors, name: response.data.message });
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);
  const update = useCallback(async () => {
    const validation = validate(data, setErrors, branchSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await editBranch(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getBranches();
      } else {
        setErrors({ ...errors, name: response.data.message });
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);
  const archive = useCallback(async () => {
    const validation = validate(data, setErrors, branchSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await archiveBranch(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getBranches();
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);
  const restore = useCallback(async () => {
    const validation = validate(data, setErrors, branchSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await restoreBranch(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getBranches();
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);

  const getAreas = async () => {
    const response = await fetchAreaList();
    if (response.data.code === 1) {
      setAreas(response?.data?.data);
    }
  };
  const getBlocks = async (area) => {
    const areaName = encodeURIComponent(area?.value);
    const response = await fetchBlockList(areaName);
    if (response.data.code === 1) {
      setBlocks(response?.data?.data);
    }
  };

  const getStreets = async (block) => {
    const areaName = encodeURIComponent(data?.area?.value);
    const blockName = encodeURIComponent(block?.value);
    const response = await fetchStreetList(areaName, blockName);
    if (response.data.code === 1) {
      setStreets(response?.data?.data);
      if (modal?.action === "edit") {
        const match = response?.data?.data?.find(
          (item) => item?.value === modal?.data?.street?.value
        );
        if (match) {
          setData({ ...data, street: modal?.data?.street });
        }
      }
    }
  };

  useEffect(() => {
    if (isAuth === true) {
      setIsLoading(false);
    }
    if (isAuth === false) {
      navigate("/");
    }
  }, [isAuth, navigate, setIsLoading]);
  useEffect(() => {
    getBranches();
  }, [page, search, getBranches]);

  useEffect(() => {
    getAreas();
  }, []);
  useEffect(() => {
    if (
      (modal?.open === true && modal?.action === "add") ||
      (modal?.open === true && modal?.action === "edit")
    ) {
      setBlocks([]);
      setData({
        ...data,
        block: null,
      });
      getBlocks(data?.area);
    }
    // eslint-disable-next-line
  }, [data?.area]);
  useEffect(() => {
    if (
      (modal?.open === true && modal?.action === "add") ||
      (modal?.open === true && modal?.action === "edit")
    ) {
      setStreets([]);
      setData({
        ...data,
        street: null,
      });
      getStreets(data?.block);
    }
    // eslint-disable-next-line
  }, [data?.block]);

  const renderBranchList = (branches) => (
    <>
      {isLoading === true ? (
        <Loader />
      ) : (
        <>
          {branches?.length > 0 ? (
            <div className="table-responsive">
              <table className="table text-nowrap">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Phone Number</th>
                    <th scope="col">Area</th>
                    <th scope="col">Block</th>
                    <th scope="col">Street</th>
                    <th scope="col">Status</th>
                    <th scope="col">Created</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {branches?.map((branch, index) => (
                    <tr key={index}>
                      <td>{branch?.name}</td>
                      <td>{branch?.phone}</td>
                      <td>{branch?.area?.value}</td>
                      <td>{branch?.block?.value}</td>
                      <td>{branch?.street?.value}</td>
                      <td>
                        <span
                          className={`badge bg-${
                            branch?.status === true ? "success" : "danger"
                          }-transparent`}
                        >
                          {branch?.status === true ? "Active" : "Suspended"}
                        </span>
                      </td>
                      <td>
                        {moment(branch?.createdAt)
                          .tz(process.env.REACT_APP_TIMEZONE)
                          .format("Do MMM, YYYY")}
                      </td>
                      <td>
                        <div className="btn-list">
                          <button
                            onClick={() => {
                              setData(branch);
                              setModal({
                                open: true,
                                action: "edit",
                                data: branch,
                              });
                            }}
                            title="Edit"
                            className="btn btn-sm btn-primary-light btn-wave waves-effect waves-light"
                          >
                            <i className="ri-edit-line me-2"></i>
                            <span>Edit</span>
                          </button>
                          {/* {branch?.isDeleted === true ? (
                            <button
                              onClick={() => {
                                setData(branch);
                                setModal({
                                  open: true,
                                  action: "restore",
                                  data: "",
                                });
                              }}
                              title="Restore"
                              className="btn btn-sm btn-icon btn-success-light btn-wave rounded-circle waves-effect waves-light"
                            >
                              <i className="ri-restart-line"></i>
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setData(branch);
                                setModal({
                                  open: true,
                                  action: "archive",
                                  data: "",
                                });
                              }}
                              title="Archive"
                              className="btn btn-sm btn-icon btn-danger-light btn-wave rounded-circle waves-effect waves-light"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )} */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="8">
                      <div className="d-flex justify-content-end">
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, value) => setPage(value)}
                        />
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center my-5">
              <img
                className="mx-auto w-25 my-5"
                src={`${process.env.REACT_APP_PUBLIC_URL}assets/images/media/backgrounds/no-content.png`}
                alt="no-content"
              />
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <div className="page">
        <Header />
        <Sidebar />
        <div className="main-content app-content">
          <div className="container-fluid">
            <div className="row mt-4">
              <div className="col-md-12">
                <div className="card custom-card">
                  <div className="card-header justify-content-between">
                    <div className="card-title">
                      <div className="d-flex justify-content-between align-items-center px-3 py- gap-2">
                        <span className="d-block fw-medium fs-15">
                          Manage Branches
                        </span>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <div>
                        <input
                          className="form-control form-control-md"
                          type="text"
                          placeholder="Search Here"
                          aria-label=".form-control-sm example"
                          onChange={(e) => setSearch(e.target.value)}
                          value={search}
                        />
                      </div>
                      <div>
                        <button
                          onClick={() => {
                            setModal({
                              open: true,
                              action: "add",
                              data: "",
                            });
                          }}
                          className="btn btn-primary btn-md me-2"
                        >
                          <i className="ri-add-line me-1 fw-medium align-middle"></i>
                          New Branch
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {renderBranchList(branches)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Begin:Add branch */}
          <div
            className={
              modal?.open === true && modal?.action === "add"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "add"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-lg"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Add branch</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Name"
                            autoComplete="off"
                            value={data?.name ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "name",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.name ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Phone Number"
                            autoComplete="off"
                            value={data?.phone ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "phone",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.phone ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Select
                            className="react-select-styled react-select-bordered react-select-sm"
                            classNamePrefix="react-select"
                            value={
                              areas
                                ?.map((item) => ({
                                  value: item?.value,
                                  label: item?.value,
                                }))
                                .find(
                                  (opt) => opt.value === data?.area?.value
                                ) ?? null
                            }
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "select",
                                "area",
                                errors,
                                setErrors
                              );
                            }}
                            options={areas?.map((item) => {
                              return {
                                value: item,
                                label: item?.value,
                              };
                            })}
                            placeholder="Select area"
                          />
                          <small className="text-danger">
                            {errors?.area ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Select
                            className="react-select-styled react-select-bordered react-select-sm"
                            classNamePrefix="react-select"
                            value={
                              blocks
                                ?.map((item) => ({
                                  value: item?.value,
                                  label: item?.value,
                                }))
                                .find(
                                  (opt) => opt.value === data?.block?.value
                                ) ?? null
                            }
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "select",
                                "block",
                                errors,
                                setErrors
                              );
                              getStreets(e?.value);
                            }}
                            options={blocks?.map((item) => {
                              return {
                                value: item,
                                label: item?.value,
                              };
                            })}
                            placeholder="Select block"
                          />
                          <small className="text-danger">
                            {errors?.block ?? ""}
                          </small>
                        </div>
                        <div className="col-md-12 mb-3">
                          <Select
                            className="react-select-styled react-select-bordered react-select-sm"
                            classNamePrefix="react-select"
                            value={
                              streets
                                ?.map((item) => ({
                                  value: item?.value,
                                  label: item?.value,
                                }))
                                .find(
                                  (opt) => opt.value === data?.street?.value
                                ) ?? null
                            }
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "select",
                                "street",
                                errors,
                                setErrors
                              );
                            }}
                            options={streets?.map((item) => {
                              return {
                                value: item,
                                label: item?.value,
                              };
                            })}
                            placeholder="Select street"
                          />
                          <small className="text-danger">
                            {errors?.street ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Building Name"
                            autoComplete="off"
                            value={data?.building ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "building",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.building ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Jadda"
                            autoComplete="off"
                            value={data?.jadda ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "jadda",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.jadda ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Apartment"
                            autoComplete="off"
                            value={data?.apartment ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "apartment",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.apartment ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Floor"
                            autoComplete="off"
                            value={data?.floor ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "floor",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.floor ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Latitude"
                            autoComplete="off"
                            value={data?.latitude ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "latitude",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.latitude ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Longitude"
                            autoComplete="off"
                            value={data?.longitude ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "longitude",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.longitude ?? ""}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 address-map">
                      <AddressMap
                        data={data}
                        setData={setData}
                        setErrors={setErrors}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      Close
                    </button>
                    <button
                      onClick={add}
                      className="btn btn-primary"
                      type="button"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Add branch */}
          {/* Begin:Edit branch */}
          <div
            className={
              modal?.open === true && modal?.action === "edit"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "edit"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-lg"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Edit branch</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <Select
                            className="react-select-styled react-select-bordered react-select-sm"
                            classNamePrefix="react-select"
                            value={
                              areas
                                ?.map((item) => ({
                                  value: item?.value,
                                  label: item?.value,
                                }))
                                .find(
                                  (opt) => opt.value === data?.area?.value
                                ) ?? null
                            }
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "select",
                                "area",
                                errors,
                                setErrors
                              );
                            }}
                            options={areas?.map((item) => {
                              return {
                                value: item,
                                label: item?.value,
                              };
                            })}
                            placeholder="Select area"
                          />
                          <small className="text-danger">
                            {errors?.area ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Select
                            className="react-select-styled react-select-bordered react-select-sm"
                            classNamePrefix="react-select"
                            value={
                              blocks
                                ?.map((item) => ({
                                  value: item?.value,
                                  label: item?.value,
                                }))
                                .find(
                                  (opt) => opt.value === data?.block?.value
                                ) ?? null
                            }
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "select",
                                "block",
                                errors,
                                setErrors
                              );
                              getStreets(e?.value);
                            }}
                            options={blocks?.map((item) => {
                              return {
                                value: item,
                                label: item?.value,
                              };
                            })}
                            placeholder="Select block"
                          />
                          <small className="text-danger">
                            {errors?.block ?? ""}
                          </small>
                        </div>
                        <div className="col-md-12 mb-3">
                          <Select
                            className="react-select-styled react-select-bordered react-select-sm"
                            classNamePrefix="react-select"
                            value={
                              streets
                                ?.map((item) => ({
                                  value: item?.value,
                                  label: item?.value,
                                }))
                                .find(
                                  (opt) => opt.value === data?.street?.value
                                ) ?? null
                            }
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "select",
                                "street",
                                errors,
                                setErrors
                              );
                            }}
                            options={streets?.map((item) => {
                              return {
                                value: item,
                                label: item?.value,
                              };
                            })}
                            placeholder="Select street"
                          />
                          <small className="text-danger">
                            {errors?.street ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Building Name"
                            autoComplete="off"
                            value={data?.building ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "building",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.building ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Jadda"
                            autoComplete="off"
                            value={data?.jadda ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "jadda",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.jadda ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Apartment"
                            autoComplete="off"
                            value={data?.apartment ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "apartment",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.apartment ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Floor"
                            autoComplete="off"
                            value={data?.floor ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "floor",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.floor ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Latitude"
                            autoComplete="off"
                            value={data?.latitude ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "latitude",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.latitude ?? ""}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control form-control-md"
                            type="text"
                            placeholder="Enter Longitude"
                            autoComplete="off"
                            value={data?.longitude ?? ""}
                            onChange={(e) => {
                              ProcessData(
                                data,
                                setData,
                                e,
                                "string",
                                "longitude",
                                errors,
                                setErrors
                              );
                            }}
                          />
                          <small className="text-danger">
                            {errors?.longitude ?? ""}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <AddressMap
                        data={data}
                        setData={setData}
                        setErrors={setErrors}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      Close
                    </button>
                    <button
                      onClick={update}
                      className="btn btn-primary"
                      type="button"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Edit branch */}
          {/* Begin:Archive */}
          <div
            className={
              modal?.open === true && modal?.action === "archive"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "archive"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-sm"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Archive branch</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <h5>Are You Sure?</h5>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="w-100 d-flex justify-content-center gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      No
                    </button>
                    <button
                      onClick={archive}
                      className="btn btn-primary"
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Archive */}
          {/* Begin:Restore */}
          <div
            className={
              modal?.open === true && modal?.action === "restore"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "restore"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-sm"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Restore branch</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <h5>Are You Sure?</h5>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="w-100 d-flex justify-content-center gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      No
                    </button>
                    <button
                      onClick={restore}
                      className="btn btn-primary"
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Restore */}
        </div>
        <Footer />
      </div>
      <ScrollToTop />
      <LoadScript />
    </>
  );
};

export default AllBranches;
